import { useState, useEffect, useCallback } from 'react';
import EntitlementsService, { EntityEntitlementsPayload } from '../services/EntitlementsService';

/**
 * Hook to read and subscribe to cached entity entitlements (mgmt/Entitlements/entity).
 * Does not fetch by itself; the app calls fetch when authenticated and on on-behalf-of change.
 * Use entityEntitlements with getReadableEntityIds / isAdministrator to drive nav visibility for non-imperator users.
 */
export function useEntityEntitlements(): {
    entityEntitlements: EntityEntitlementsPayload | null;
    loading: boolean;
    error: string | null;
    refetch: () => Promise<EntityEntitlementsPayload | null>;
} {
    const [entityEntitlements, setEntityEntitlements] = useState<EntityEntitlementsPayload | null>(
        () => EntitlementsService.getCachedEntityEntitlements()
    );
    const [loading, setLoading] = useState(EntitlementsService.isLoading());
    const [error, setError] = useState<string | null>(EntitlementsService.getError());

    useEffect(() => {
        const sync = () => {
            setEntityEntitlements(EntitlementsService.getCachedEntityEntitlements());
            setLoading(EntitlementsService.isLoading());
            setError(EntitlementsService.getError());
        };
        sync();
        const unsubscribe = EntitlementsService.subscribe(sync);
        return unsubscribe;
    }, []);

    const refetch = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const result = await EntitlementsService.fetchEntityEntitlements();
            setEntityEntitlements(EntitlementsService.getCachedEntityEntitlements());
            return result;
        } catch (e) {
            setError(EntitlementsService.getError());
            return null;
        } finally {
            setLoading(EntitlementsService.isLoading());
        }
    }, []);

    return {
        entityEntitlements,
        loading,
        error,
        refetch
    };
}
